import {
  EOnboaringFormStep,
  ERegion,
  getOnboardingStepsByKey,
  EOnboardingKey,
  STEPS_TO_SKIP_IF_CONTRACT_SIGNED
} from '@constants';
import { Form } from 'antd';
import { CarouselRef } from 'antd/es/carousel';
import { useMemo } from 'react';
import { create } from 'zustand';

import { useOnboardingForm } from '@/providers';
import {
  useMutatePlanification,
  useMutateMySubscriptions,
  useProfile,
  useAcademy,
  useMutateProfile,
  useMutateUserInfos,
  useUserOnboarding
} from '@/store';
import { getUserSubverticals } from '@/utils/getUserSubverticals';

type IStore = {
  stepIndex: number;
  isOnboardingStarted: boolean;
  carouselRef: React.RefObject<CarouselRef>;
  examGroupDate: Date | undefined;
  loadingAfterContract: boolean;
  setExamGroupDate: (examGroupDate: Date | undefined) => void;
  setStepIndex: (stepIndex: number) => void;
  startOnboarding: (initialStepIndex?: number) => void;
  startLoadingAfterContract: () => void;
  stopLoadingAfterContract: () => void;
};

const store = create<IStore>(set => ({
  stepIndex: 0,
  isOnboardingStarted: false,
  carouselRef: { current: null },
  examGroupDate: undefined,
  loadingAfterContract: false,
  setExamGroupDate: examGroupDate => set({ examGroupDate }),
  setStepIndex: stepIndex => set({ stepIndex }),
  startOnboarding: (initialStepIndex?: number) => {
    set({
      isOnboardingStarted: true,
      stepIndex: initialStepIndex ?? 0
    });
  },
  startLoadingAfterContract: () => {
    set({ loadingAfterContract: true });
  },
  stopLoadingAfterContract: () => {
    set({ loadingAfterContract: false });
  }
}));

export const useOnboardingFormLogic = () => {
  const {
    stepIndex,
    setStepIndex,
    isOnboardingStarted,
    startOnboarding,
    carouselRef,
    examGroupDate,
    setExamGroupDate,
    startLoadingAfterContract,
    stopLoadingAfterContract,
    loadingAfterContract
  } = store();
  const { setMyVisibleSubscriptions } = useMutateMySubscriptions();
  const { profile } = useProfile();
  const academyId = profile?.academy?._id;
  const { academy } = useAcademy(academyId);
  const { isContractSigned } = useUserOnboarding();
  const { createPlanification, isWorking: isPlanificationLoading } = useMutatePlanification();
  const { updateProfile, isWorking: isProfileUpdating } = useMutateProfile();
  const { updateUserInfo, isWorking: isUserInfoUpdating } = useMutateUserInfos(profile?._id || '');
  const form = useOnboardingForm();
  const planification = Form.useWatch(['planification'], form);

  const shouldCreatePlanificationOnThisStep = () => {
    const key = academy?.onboarding?.onboardingKey as EOnboardingKey | undefined;
    if (key === EOnboardingKey.FP) return currentStep === EOnboaringFormStep.STUDY_PLAN_FP;
    if (key === EOnboardingKey.UNI || key === EOnboardingKey.OPOS)
      return currentStep === EOnboaringFormStep.STUDY_PLAN;
    return false;
  };

  const handleStartOnboarding = () => {
    if (isContractSigned) {
      const firstValidStepIndex = allSteps.findIndex(
        step => !STEPS_TO_SKIP_IF_CONTRACT_SIGNED.includes(step)
      );
      const initialIndexAfterContract = firstValidStepIndex !== -1 ? firstValidStepIndex : 0;
      startOnboarding(initialIndexAfterContract);
    } else {
      startOnboarding(0);
    }
  };

  const navigateToNextStep = () => {
    const nextIndex = stepIndex + 1;
    setStepIndex(nextIndex);
    carouselRef.current?.goTo(nextIndex);
  };

  const handleNextStep = async () => {
    if (!carouselRef.current || !profile) return;

    const nextStep = allSteps[stepIndex + 1];
    if (nextStep === EOnboaringFormStep.SIGN_CONTRACT) {
      const values = form.getFieldsValue();
      const { userInfo, planification: _discardPlanification, ...profileValues } = values;

      const filteredUserInfo = userInfo
        ? {
            ...userInfo,
            subjects: userInfo.subjects
              ?.filter(
                (subject: { note?: number }) => subject.note !== 0 && subject.note !== undefined
              )
              .map((subject: { note?: number }) => ({
                ...subject,
                note: Number(subject.note)
              }))
          }
        : undefined;

      updateProfile(profileValues, {
        onSuccess: () => {
          if (filteredUserInfo) {
            updateUserInfo(filteredUserInfo, {
              onSuccess: () => navigateToNextStep()
            });
          } else {
            navigateToNextStep();
          }
        }
      });

      return;
    }

    if (shouldCreatePlanificationOnThisStep()) {
      createPlanification(
        {
          ...planification,
          userId: profile._id,
          includeAllActivities: true,
          endDate: planification?.endDate ? new Date(planification?.endDate) : undefined,
          restDates: (planification?.restDates ?? []).map((date: Date) => new Date(date)),
          restWeekdays: planification?.restWeekdays ?? []
        },
        {
          onSuccess: async () => {
            const selected = planification?.subscriptions;
            if (Array.isArray(selected) && selected.length > 0) {
              await setMyVisibleSubscriptions(selected);
            }
            navigateToNextStep();
          }
        }
      );

      return;
    }
    if (stepIndex === allSteps.length - 1) {
      form.submit();
      return;
    }

    navigateToNextStep();
  };

  const handlePreviousStep = () => {
    if (!carouselRef.current || stepIndex === 0) return;
    const prevIndex = stepIndex - 1;
    setStepIndex(prevIndex);
    carouselRef.current.goTo(prevIndex);
  };

  const allSteps = useMemo(() => {
    const region = profile?.regionConfig ?? ERegion.ES;
    const key = academy?.onboarding?.onboardingKey;
    const userSubverticals = getUserSubverticals(profile);

    return getOnboardingStepsByKey(key, region, userSubverticals);
  }, [profile?.regionConfig, profile?.tags, academy?.onboarding?.onboardingKey]);

  const currentStep = allSteps[stepIndex];
  const stepLength = allSteps.length;

  return {
    form,
    allSteps,
    carouselRef,
    currentStep,
    handleNextStep,
    isOnboardingStarted,
    startOnboarding: handleStartOnboarding,
    setStepIndex,
    handlePreviousStep,
    stepLength,
    stepIndex,
    examGroupDate,
    setExamGroupDate,
    startLoadingAfterContract,
    stopLoadingAfterContract,
    loadingAfterContract,
    isNextStepLoading: isPlanificationLoading || isProfileUpdating || isUserInfoUpdating,
    isContractSigned
  };
};
